import {HUBSPOT_SCHEDULE_A_TOUR_URL} from '@components/leadFlowModal/constants'
import {RENTAL_APPLICATION_URLS} from '@components/rentalApplication/constants'
import analytics from '@shared-utils/analytics'
import {ProductSurface} from '@shared-utils/types'
import {Link} from 'gatsby'
import React from 'react'

import CuldesacLogo from '../../svg/culdesac-logo.svg'
import Icon from '../ui/SVGIcon'

/**
 * @returns The social icons in a neat row
 */
export const SocialIcons: React.FC = () => (
  <div className="flex mb-6">
    <a
      href="https://twitter.com/culdesac"
      onClick={() => analytics.trackClick('twitter-icon', 'web', ProductSurface.LeasingFooter)}
    >
      <Icon alt="Twitter" className="w-6 h-6 mr-4" name="twitter" />
    </a>
    <a
      href="https://www.instagram.com/liveculdesac/"
      onClick={() => analytics.trackClick('instagram-icon', 'web', ProductSurface.LeasingFooter)}
    >
      <Icon alt="Instagram" className="w-6 h-6 mr-4" name="instagram" />
    </a>
    <a
      href="https://www.facebook.com/LiveCuldesac/"
      onClick={() => analytics.trackClick('facebook-icon', 'web', ProductSurface.LeasingFooter)}
    >
      <Icon alt="Facebook" className="w-6 h-6 mr-4" name="facebook" />
    </a>
    <a
      href="https://www.linkedin.com/company/liveculdesac/"
      onClick={() => analytics.trackClick('linkedin-icon', 'web', ProductSurface.LeasingFooter)}
    >
      <Icon alt="LinkedIn" className="w-6 h-6 mr-4" name="linkedin" />
    </a>
    <a
      href="https://www.tiktok.com/@liveculdesac"
      onClick={() => analytics.trackClick('tiktok-icon', 'web', ProductSurface.LeasingFooter)}
    >
      <Icon alt="TikTok" className="w-6 h-6" name="tiktok" />
    </a>
  </div>
)

/**
 * Render the PhoneNumbers to be included in the footer
 * @returns Valid JSX showing the phone numbers
 */
export const PhoneNumbers: React.FC = () => (
  <div className="text-md">
    <address className="flex not-italic">
      <a
        className="whitespace-nowrap tw-dark-link"
        href="tel:+14802106004"
        onClick={() => analytics.trackClick('phone-number-link', 'web', ProductSurface.LeasingFooter)}
      >
        <span className="sr-only">Contact us at </span>(480) 210 - 6004
      </a>
    </address>
  </div>
)

export const PrivacyPolicySection: React.FC = () => (
  <div className="flex flex-col items-center mb-8 lg:mb-0 text-md text-black-lightest lg:flex-row">
    <p className="mb-8 lg:mb-0 lg:mr-4">©{new Date().getFullYear()} Culdesac All Rights Reserved</p>
    <Link
      className="mb-8 lg:mb-0 lg:mr-4 tw-dark-link"
      onClick={() => analytics.trackClick('privacy-policy-link', 'web', ProductSurface.LeasingFooter)}
      rel="nofollow"
      to="/privacy-policy"
    >
      Privacy Policy
    </Link>
    <Link className="mb-8 lg:mb-0 lg:mr-4 tw-dark-link" rel="nofollow" to="/terms-and-conditions">
      Terms & Conditions
    </Link>
    <Link className="mb-8 lg:mb-0 lg:mr-4 tw-dark-link" rel="nofollow" to="/tempe/land-acknowledgment">
      Land Acknowledgment
    </Link>
    <span className="flex flex-row items-center mb-8 lg:mb-0 lg:mr-4">
      <Icon alt="" name="equal-housing-opportunity-logo" />
      <p className="ml-2">Equal Housing Opportunity</p>
    </span>
    <span className="flex flex-row items-center mb-8 lg:mb-0 lg:mr-4">
      <Icon alt="" name="accessibility-icon" />
      <p className="ml-2">ADA Compliant</p>
    </span>
    <span className="flex flex-row items-center mb-8 lg:mb-0 lg:mr-4">
      <Icon alt="" name="animal-print" />
      <p className="ml-2">Pet Friendly</p>
    </span>
  </div>
)

interface FooterProps {
  className?: string
}

/**
 * The main culdesac.com site footer.
 * @returns Footer JSX
 */
const Footer: React.FC<FooterProps> = ({className}) => (
  <footer className={`w-full px-8 py-8 lg:px-16 bg-white dd-privacy-allow ${className ? className : 'mt-12 lg:mt-20'}`}>
    <div className="flex flex-col items-center lg:items-start lg:justify-items-start lg:flex-row">
      <div className="flex flex-col items-center mb-6 lg:mb-0 lg:items-start lg:w-1/4">
        <CuldesacLogo className="w-32 pb-1 mb-6 lg:w-32" />
        <SocialIcons />
        <PhoneNumbers />
      </div>
      <div className="flex flex-col items-center mb-6 lg:mb-0 lg:text-left lg:items-start lg:w-1/4">
        <h4 className="mb-3 h4">Leasing Office Hours</h4>
        <p className="mb-1 font-light text-md lg:mt-3 lg:mb-2">Mon – Fri&emsp;10 AM – 5 PM</p>
        <p className="my-1 font-light text-md lg:my-2">Sat – Sun&emsp;10 AM – 4 PM</p>
      </div>
      <div className="items-center mb-6 text-center sm:text-left sm:items-start grid grid-cols-2 gap-4 sm:grid-cols-3 lg:ml-auto lg:mb-0 text-md lg:w-1/2">
        <div className="contents">
          <h4 className="font-semibold">
            <Link
              className="tw-dark-link"
              onClick={() => analytics.trackClick('tempe-footer-section-link', 'web', ProductSurface.LeasingFooter)}
              to="/tempe"
            >
              Tempe
            </Link>
          </h4>
          <h4 className="font-semibold">
            <a
              className="tw-dark-link"
              href="/about"
              onClick={() => analytics.trackClick('company-footer-section-link', 'web', ProductSurface.LeasingFooter)}
            >
              Company
            </a>
          </h4>
          <Link
            className="font-light tw-dark-link"
            onClick={() => analytics.trackClick('floor-plans-link', 'web', ProductSurface.LeasingFooter)}
            to={RENTAL_APPLICATION_URLS.UNIT_SELECTION_URL}
          >
            Available Floor Plans
          </Link>
          <a
            className="font-light tw-dark-link"
            href="/about"
            onClick={() => analytics.trackClick('about-link', 'web', ProductSurface.LeasingFooter)}
          >
            About Us
          </a>
          <a
            className="font-light tw-dark-link"
            href={HUBSPOT_SCHEDULE_A_TOUR_URL}
            onClick={() => analytics.trackClick('schedule-tour-link', 'web', ProductSurface.LeasingFooter)}
          >
            Schedule a tour
          </a>
          <a
            className="font-light tw-dark-link"
            href="https://shop.culdesac.com"
            onClick={() => analytics.trackClick('shop-link', 'web', ProductSurface.LeasingFooter)}
          >
            Merch
          </a>
          <Link
            className="font-light tw-dark-link"
            onClick={() => analytics.trackClick('tempe-page-link', 'web', ProductSurface.LeasingFooter)}
            to="/tempe"
          >
            Neighborhood
          </Link>
          <Link
            className="font-light tw-dark-link"
            onClick={() => analytics.trackClick('careers-link', 'web', ProductSurface.LeasingFooter)}
            to="/careers"
          >
            Careers
          </Link>
          <Link
            className="font-light tw-dark-link"
            onClick={() => analytics.trackClick('map-link', 'web', ProductSurface.LeasingFooter)}
            to="/tempe/map"
          >
            Map
          </Link>
          <div className="hidden"></div>
          <a
            className="font-light tw-dark-link"
            href="https://prospectportal.culdesac.com/Apartments/module/photos/property%5Bid%5D/1279098/"
            onClick={() => analytics.trackClick('photo-gallery', 'web', ProductSurface.LeasingFooter)}
            rel="noreferrer noopener"
            target="_blank"
          >
            Photo Gallery
          </a>
        </div>
        {/* <h4>
          <Link
            className="font-semibold tw-dark-link"
            onClick={() => analytics.trackClick('dill-footer-section-link', 'web', ProductSurface.LeasingFooter)}
            to="https://1070dill.com/"
          >
            Atlanta
          </Link>
        </h4>
        <Link
          className="font-light tw-dark-link sm:row-span-7"
          onClick={() => analytics.trackClick('dill-link', 'web', ProductSurface.LeasingFooter)}
          to="https://1070dill.com/"
        >
          1070 Dill Ave Townhomes
        </Link> */}
        {/* <Link
          className="font-light tw-dark-link sm:row-span-7"
          onClick={() => analytics.trackClick('murphy-crossing-link', 'web', ProductSurface.LeasingFooter)}
          to="atlanta/murphy-crossing"
        >
          Murphy Crossing
        </Link> */}
      </div>
    </div>
    <div className="my-8 border-t border-gray-200" />
    <PrivacyPolicySection />
  </footer>
)

export default Footer
